/**
 * Shift Rule Role Helper - UI-Abstraktion für Prioritäten
 *
 * Übersetzt technische Prioritätszahlen in verständliche Rollen:
 * - "Basis" = Standardregel (priority = 0, keine zeitliche Einschränkung)
 * - "Ausnahme" = Override-Regel (priority > 0 oder zeitlich eingeschränkt)
 *
 * Das Datenmodell (priority: number) bleibt unverändert!
 */

export const BASE_PRIORITY = 0;
export const OVERRIDE_PRIORITY = 100;

export type RuleRole = 'base' | 'override';

export interface ShiftRuleForRole {
  priority: number;
  validFrom?: string | Date | null;
  validUntil?: string | Date | null;
}

/**
 * Bestimmt die Rolle einer Schichtregel basierend auf priority und Gültigkeit.
 *
 * Logik:
 * - priority > BASE_PRIORITY → Ausnahme
 * - Hat validFrom ODER validUntil gesetzt → Ausnahme (zeitlich begrenzt)
 * - Sonst → Basis
 */
export function getRuleRole(rule: ShiftRuleForRole): RuleRole {
  if (rule.priority > BASE_PRIORITY) {
    return 'override';
  }
  if (rule.validFrom || rule.validUntil) {
    return 'override';
  }
  return 'base';
}

/**
 * Formatiert die Rolle als deutsches Label.
 */
export function formatRoleLabel(rule: ShiftRuleForRole): string {
  return getRuleRole(rule) === 'base' ? 'Basis' : 'Ausnahme';
}

/**
 * Gibt die empfohlene Priority basierend auf der Rolle zurück.
 */
export function getPriorityForRole(role: RuleRole): number {
  return role === 'base' ? BASE_PRIORITY : OVERRIDE_PRIORITY;
}

/**
 * Berechnet die minimale Priority, die zum Gewinnen gegen Konfliktregeln nötig ist.
 *
 * @param conflictingPriorities - Prioritäten der konfliktierenden Regeln
 * @returns Mindest-Priority (max + 1, mindestens OVERRIDE_PRIORITY)
 */
export function getWinningPriority(conflictingPriorities: number[]): number {
  if (conflictingPriorities.length === 0) {
    return OVERRIDE_PRIORITY;
  }
  const maxConflict = Math.max(...conflictingPriorities);
  return Math.max(maxConflict + 1, OVERRIDE_PRIORITY);
}

/**
 * Vergleicht zwei Regeln und gibt den Gewinner zurück.
 *
 * @returns 'a' wenn a gewinnt, 'b' wenn b gewinnt, 'tie' bei Gleichstand
 */
export function compareRulePriority(
  a: { priority: number; code: string },
  b: { priority: number; code: string }
): 'a' | 'b' | 'tie' {
  if (a.priority > b.priority) return 'a';
  if (b.priority > a.priority) return 'b';
  // Bei Gleichstand: alphabetisch nach code
  const codeCompare = a.code.localeCompare(b.code);
  if (codeCompare < 0) return 'a';
  if (codeCompare > 0) return 'b';
  return 'tie';
}

/**
 * Erklärt, warum eine Regel gewinnt (für UI).
 */
export function explainWinner(
  winner: { priority: number; code: string; name: string },
  loser: { priority: number; code: string; name: string },
  winnerRole: RuleRole,
  loserRole: RuleRole
): string {
  if (winner.priority > loser.priority) {
    if (winnerRole === 'override' && loserRole === 'base') {
      return 'Ausnahme überschreibt Basis';
    }
    return `Höhere Priorität (${winner.priority} > ${loser.priority})`;
  }
  if (winner.priority === loser.priority) {
    return `Gleichstand bei Priorität, alphabetisch nach Code ("${winner.code}" < "${loser.code}")`;
  }
  return 'Gewinner durch Regellogik';
}

/**
 * Formatiert die Gültigkeit als lesbaren String.
 */
export function formatValidity(
  validFrom: string | Date | null | undefined,
  validUntil: string | Date | null | undefined
): string {
  const formatDate = (d: string | Date | null | undefined): string | null => {
    if (!d) return null;
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const from = formatDate(validFrom);
  const until = formatDate(validUntil);

  if (from && until) {
    return `${from} – ${until}`;
  }
  if (from) {
    return `ab ${from}`;
  }
  if (until) {
    return `bis ${until}`;
  }
  return 'unbegrenzt';
}
